/**
 * Algolia Autocomplete portals its panel to <body>. On keyboard navigation
 * (ArrowUp/ArrowDown) it calls `scrollIntoViewIfNeeded` on the active suggestion.
 * Because the panel sits at the end of <body>, Chromium scrolls the whole
 * document to the bottom even though the panel is visually fixed near the input —
 * the page "loses" its content while the user only previews a suggestion.
 *
 * This guard restores the document scroll position synchronously after Algolia's
 * keydown handler runs, keeping the in-panel scroll (a different scroll container)
 * untouched. Installed once, it covers every autocomplete instance.
 */

let installed = false;

function isAutocompleteKeyboardNav(event: KeyboardEvent): boolean {
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") {
        return false;
    }

    const target = event.target as Element | null;
    return Boolean(target?.closest?.(".aa-Autocomplete"));
}

export function installAutocompleteScrollGuard(): void {
    if (installed || typeof document === "undefined") {
        return;
    }
    installed = true;

    document.addEventListener(
        "keydown",
        (event: KeyboardEvent) => {
            if (!isAutocompleteKeyboardNav(event)) {
                return;
            }

            const { scrollX, scrollY } = window;
            requestAnimationFrame(() => {
                if (window.scrollX !== scrollX || window.scrollY !== scrollY) {
                    window.scrollTo(scrollX, scrollY);
                }
            });
        },
        true,
    );
}
